import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Fade,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Container,
  FormHelperText,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { differenceInMonths, differenceInYears } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import {
  saveViolationAction,
  getViolationsByIdAction,
  updatedViolationAction,
  getAllViolationsActions,
  deleteViolationByIdAction
} from '../../redux/actions/driverInfoActions';
import dayjs from 'dayjs';
import DatePickerViolation from './DatePickerViolation';

// Styled Components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '20px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    overflow: 'hidden',
    background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: 'linear-gradient(135deg, #FFCB3D 0%, #FFCB3D 100%)',
  color: 'Black',
  padding: '14px 32px',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, #FFCB3D, #FFCB3D, #FFCB3D, #FFCB3D)',
  },
}));

const ViolationCard = styled(Card)(({ theme, isediting }) => ({
  marginTop: '10px',
  marginBottom: '20px',
  borderRadius: '16px',
  border: isediting ? '2px solid #667eea' : '1px solid #e2e8f0',
  boxShadow: isediting
    ? '0 10px 25px -5px rgba(102, 126, 234, 0.25)'
    : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  background: isediting
    ? 'linear-gradient(145deg, #f8faff 0%, #ffffff 100%)'
    : '#ffffff',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15)',
  },
}));

const ActionButton = styled(IconButton)(({ theme, variant = 'default' }) => ({
  borderRadius: '12px',
  padding: '9px',
  transition: 'all 0.2s ease-in-out',
  ...(variant === 'save' && {
    background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)',
    color: 'white',
    '&:hover': {
      background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
      transform: 'scale(1.05)',
    },
  }),
  ...(variant === 'edit' && {
    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
    color: 'white',
    '&:hover': {
      background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
      transform: 'scale(1.05)',
    },
  }),
  ...(variant === 'delete' && {
    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    color: 'white',
    '&:hover': {
      background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
      transform: 'scale(1.05)',
    },
  }),
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#f8fafc',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: '#f1f5f9',
    },
    '&.Mui-focused': {
      backgroundColor: '#ffffff',
      '& fieldset': {
        borderColor: '#667eea',
        borderWidth: '2px',
      },
    },
  },
  '& .MuiInputLabel-root': {
    color: '#64748b',
    fontWeight: 500,
  },
}));

// const StyledTextField = styled(TextField)(({ theme }) => ({
//   '& .MuiOutlinedInput-root': {
//     borderRadius: '12px',
//     backgroundColor: '#f8fafc',
//     transition: 'all 0.2s ease-in-out',
//     '&:hover': {
//       backgroundColor: '#f1f5f9',
//     },
//     '&.Mui-focused': {
//       backgroundColor: '#ffffff',
//       '& fieldset': {
//         borderColor: '#667eea',
//         borderWidth: '2px',
//       },
//     },
//   },
//   '& .MuiInputLabel-root': {
//     color: '#64748b',
//     fontWeight: 500,
//   },
// }));

const AddButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  padding: '12px 24px',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
  '&:hover': {
    background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
    transform: 'translateY(-2px)',
  },
}));

const CancelButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  padding: '12px 24px',
  border: '2px solid #e2e8f0',
  color: '#64748b',
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': {
    border: '2px solid #cbd5e1',
    backgroundColor: '#f8fafc',
  },
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '300px',
  gap: '16px',
}));

const TimeSinceChip = styled(Chip)(({ theme }) => ({
  backgroundColor: '#f0f9ff',
  color: '#0369a1',
  fontWeight: 600,
  borderRadius: '20px',
  '& .MuiChip-icon': {
    color: '#0369a1',
  },
}));

const ViolationNumberChip = styled(Chip)(({ theme }) => ({
  background: theme.palette.primary.main,
  color: 'Black',
  fontWeight: 600,
  fontSize: '0.875rem',
}));

const DriverViolationsDialog = ({ open, handleClose, driverId, onSave, existingViolations = [] }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  // const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  
  const [violations, setViolations] = useState([]);
  const [editModes, setEditModes] = useState({});
  const [dataLoaded, setDataLoaded] = useState(false);
  const dispatch = useDispatch();
  const allViolations = useSelector((state) => state.driverInfo.violations || []);
  const driverViolations = useSelector((state) => state.driverInfo.allViolations || []);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [violationErrors, setViolationErrors] = useState({});
  const isLoadingDriverViolations = useSelector((state) => state.driverInfo.isLoadingViolations || false);
 const currentDriverId = useSelector((state) => state.driverInfo.currentDriverId);
  const clearError = (violationId, field) => {
    setViolationErrors((prev) => ({
      ...prev,
      [violationId]: {
        ...prev[violationId],
        [field]: ''
      }
    }));
  };

  // Fetch all violation codes when dialog opens
  // Update the first useEffect that handles dialog opening
useEffect(() => {
  if (open && driverId) {
    // Reset all local state
    setDataLoaded(false);
    setViolations([]);
    setEditModes({});
    setViolationErrors({});
 dispatch({ type: "CLEAR_DRIVER_VIOLATIONS" });
    // Use setTimeout to ensure state is cleared before fetching
      setTimeout(() => {
        dispatch(getAllViolationsActions());
        dispatch(getViolationsByIdAction(driverId));
      }, 100);
  }
  return () => {
      // Cleanup on unmount or when dialog closes
      if (!open) {
        setDataLoaded(false);
        setViolations([]);
        setEditModes({});
        setViolationErrors({});
      }
    };
}, [open, driverId, dispatch]);

  // Wait for data to be loaded and then initialize
  useEffect(() => {
  if (!open || isSaving || isDeleting) return;

  // Only proceed if we have the violation codes loaded
  // if (allViolations.length === 0) return;

  // Check if we're still fetching driver violations
  if (isLoadingDriverViolations) return;
 if (currentDriverId !== driverId) return;

  // Now we can safely process the data
  if (!dataLoaded) {
    let initialViolations = [];
    let initialEditModes = {};

    if (driverViolations.length > 0) {
      // Process driver violations
      initialViolations = driverViolations.map(v => {
        const currentDate = new Date();
        let timeSince = '';
        let date = null;

        if (v.violationDt) {
          const parsedDate = dayjs(v.violationDt, 'MM-DD-YYYY');
          if (parsedDate.isValid()) {
            date = parsedDate;
            const violationDate = parsedDate.toDate();
            const totalYears = differenceInYears(currentDate, violationDate);
            const totalMonths = differenceInMonths(currentDate, violationDate);
            const remainingMonths = totalMonths % 12;
            timeSince = `${totalYears} year${totalYears !== 1 ? 's' : ''} and ${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
          }
        }

        const violation = {
          id: v.id,
          violationCode: v.accidentViolationCd || '',
          date,
          year: date ? date.format('YYYY') : '',
          month: date ? date.format('MMMM') : '',
          timeSince,
          did: driverId,
          description: allViolations.find(opt => opt.violationCode === v.accidentViolationCd)?.violationDescription || '',
          isExisting: true
        };

        initialEditModes[v.id] = false;
        return violation;
      });
    } else if (existingViolations && existingViolations.length > 0) {
      // Process existing violations (fallback)
      initialViolations = existingViolations.map(violation => {
        const currentDate = new Date();
        let timeSince = '';
        let date = null;

        if (violation.violationDt) {
          const parsedDate = dayjs(violation.violationDt, 'MM-DD-YYYY');
          if (parsedDate.isValid()) {
            date = parsedDate;
            const violationDate = parsedDate.toDate();
            const totalYears = differenceInYears(currentDate, violationDate);
            const totalMonths = differenceInMonths(currentDate, violationDate);
            const remainingMonths = totalMonths % 12;
            timeSince = `${totalYears} year${totalYears !== 1 ? 's' : ''} and ${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
          }
        }

        const mappedViolation = {
          id: violation.id || Date.now(),
          violationCode: violation.accidentViolationCd || '',
          date,
          year: date ? date.format('YYYY') : '',
          month: date ? date.format('MMMM') : '',
          timeSince,
          did: driverId,
          description: allViolations.find(opt => opt.violationCode === violation.accidentViolationCd)?.violationDescription || '',
          isExisting: true
        };

        initialEditModes[mappedViolation.id] = false;
        return mappedViolation;
      });
    }

    // If no violations, add empty one
    if (initialViolations.length === 0) {
      const newViolation = {
        violationCode: '',
        date: null,
        year: '',
        month: '',
        timeSince: '',
        id: Date.now(),
        did: driverId,
        isExisting: false
      };
      initialViolations = [newViolation];
      initialEditModes[newViolation.id] = true;
    }

    setViolations(initialViolations);
    setEditModes(initialEditModes);
    setDataLoaded(true);

    // Only call onSave if we have existing violations
    if (initialViolations.some(v => v.isExisting)) {
      onSave(driverId, initialViolations.filter(v => v.isExisting));
    }
  }
}, [open, allViolations, driverViolations, existingViolations, driverId, onSave, dataLoaded, isSaving, isDeleting, isLoadingDriverViolations]);

  const handleAddViolation = () => {
    const newViolation = {
      violationCode: '',
      date: null,
      year: '',
      month: '',
      timeSince: '',
      id: Date.now(),
      did: driverId,
      isExisting: false
    };

    setViolations(prev => [...prev, newViolation]);
    setEditModes(prev => ({ ...prev, [newViolation.id]: true }));
  };

  const handleRemoveViolation = async (index) => {
    const violationToRemove = violations[index];

    if (violationToRemove.isExisting && violationToRemove.id) {
      try {
        setIsDeleting(true);
        await dispatch(deleteViolationByIdAction(violationToRemove.id));

        const updatedViolations = violations.filter((_, i) => i !== index);

        if (updatedViolations.length === 0) {
          const emptyViolation = {
            violationCode: '',
            date: null,
            year: '',
            month: '',
            timeSince: '',
            id: Date.now(),
            did: driverId,
            isExisting: false
          };
          setViolations([emptyViolation]);
          setEditModes({ [emptyViolation.id]: true });
        } else {
          setViolations(updatedViolations);
          setEditModes(prev => {
            const updatedModes = { ...prev };
            delete updatedModes[violationToRemove.id];
            return updatedModes;
          });
        }

        setTimeout(() => {
          dispatch(getViolationsByIdAction(driverId));
          setIsDeleting(false);
        }, 500);

      } catch (error) {
        console.error('Failed to delete violation:', error);
        setIsDeleting(false);
        alert('Failed to delete violation. Please try again.');
        return;
      }
    } else {
      const updatedViolations = violations.filter((_, i) => i !== index);

      if (updatedViolations.length === 0) {
        const emptyViolation = {
          violationCode: '',
          date: null,
          year: '',
          month: '',
          timeSince: '',
          id: Date.now(),
          did: driverId,
          isExisting: false
        };
        setViolations([emptyViolation]);
        setEditModes({ [emptyViolation.id]: true });
      } else {
        setViolations(updatedViolations);
        setEditModes(prev => {
          const updatedModes = { ...prev };
          delete updatedModes[violationToRemove.id];
          return updatedModes;
        });
      }

      const existingViolations = updatedViolations.filter(v => v.isExisting);
      if (existingViolations.length > 0) {
        onSave(driverId, existingViolations);
      }
    }
  };

  const handleViolationChange = useCallback((index, field, value) => {
    setViolations(prev => {
      const updatedViolations = [...prev];
      updatedViolations[index][field] = value;

      if (field === 'date' && value && value.isValid()) {
        try {
          updatedViolations[index].year = value.format('YYYY');
          updatedViolations[index].month = value.format('MMMM');

          const currentDate = new Date();
          const violationDate = value.toDate();
          const totalYears = differenceInYears(currentDate, violationDate);
          const totalMonths = differenceInMonths(currentDate, violationDate);
          const remainingMonths = totalMonths % 12;

          updatedViolations[index].timeSince = `${totalYears} year${totalYears !== 1 ? 's' : ''} and ${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
        } catch (error) {
          console.error("Date parsing error:", error);
        }
      }

      if (field === 'violationCode') {
        updatedViolations[index].description = allViolations.find(opt => opt.violationCode === value)?.violationDescription || '';
      }

      return updatedViolations;
    });
  }, [allViolations]);

  const handleSaveViolation = useCallback(
    async (index) => {
      const violation = violations[index];
      let hasError = false;
      const newErrors = {};

      if (!violation.violationCode?.trim()) {
        newErrors.violationCode = 'Violation Description is required';
        hasError = true;
      }

      if (!violation.date || !violation.date.isValid()) {
        newErrors.date = 'Valid Violation Date is required';
        hasError = true;
      }

      if (hasError) {
        setViolationErrors((prev) => ({
          ...prev,
          [violation.id]: newErrors
        }));
        return;
      }

      setIsSaving(true);

      const formattedDate = violation.date.format('MM-DD-YYYY');
      const matchingViolation = allViolations.find(
        (v) => v.violationCode === violation.violationCode
      );

      const payload = {
        violationDt: formattedDate,
        accidentViolationCd: violation.violationCode,
        did: driverId,
        ...(matchingViolation && { commonViolationId: matchingViolation.commonViolationId })
      };

      try {
        if (violation.isExisting) {
          await dispatch(updatedViolationAction({ ...payload, id: violation.id }));
        } else {
          await dispatch(saveViolationAction(payload));
        }

        setViolations((prev) => {
          const updatedViolations = [...prev];
          updatedViolations[index].isExisting = true;
          return updatedViolations;
        });

        setEditModes((prev) => ({
          ...prev,
          [violation.id]: false
        }));

        setViolationErrors((prev) => ({
          ...prev,
          [violation.id]: {}
        }));

        setTimeout(() => {
          dispatch(getViolationsByIdAction(driverId));
          setIsSaving(false);
        }, 1000);
      } catch (error) {
        console.error('Failed to save violation:', error);
        setIsSaving(false);
        setViolationErrors((prev) => ({
          ...prev,
          [violation.id]: {
            ...prev[violation.id],
            general: 'Failed to save violation. Please try again.'
          }
        }));
      }
    },
    [dispatch, driverId, violations, allViolations]
  );

  const handleEditViolation = useCallback((index) => {
    const violationId = violations[index].id;
    setEditModes(prev => ({
      ...prev,
      [violationId]: true
    }));
  }, [violations]);

  const handleDialogClose = () => {
    setDataLoaded(false);
    setViolations([]);
    setEditModes({});
    setIsSaving(false);
    setIsDeleting(false);
    handleClose();
  };

  if (open && (!dataLoaded || isLoadingDriverViolations) && violations.length === 0) {
    return (
      <Dialog open={open} onClose={handleDialogClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Typography variant="h5" fontWeight="600" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningAmberIcon />
            Driver Violations
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ padding: 0 }}>
          <LoadingContainer>
            <CircularProgress size={48} sx={{ color: '#667eea' }} />
            <Typography variant="h6" color="#64748b" fontWeight="500">
              Loading violations...
            </Typography>
          </LoadingContainer>
        </DialogContent>
        <DialogActions sx={{ padding: '24px 32px', background: '#f8fafc' }}>
          <CancelButton onClick={handleDialogClose}>Cancel</CancelButton>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Typography variant={isMobile ? "h6" : "h5"} fontWeight="600" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon fontSize={isMobile ? "small" : "medium"} />
          Driver Violations Management
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
          Manage and track driver violation records
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ padding: isMobile ? '16px' : '32px', backgroundColor: '#f8fafc' }}>
        <Container maxWidth="lg" sx={{ padding: isMobile ? '0' : 'inherit' }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            {violations.map((violation, index) => (
              <Fade in={true} timeout={300 + index * 100} key={violation.id}>
                <ViolationCard isediting={editModes[violation.id] ? 'true' : undefined}>
                  <CardContent sx={{ padding: isMobile ? '8px' : '16px' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <ViolationNumberChip
                        label={`Violation #${index + 1}`}
                        size={isMobile ? "small" : "medium"}
                      />
                    </Box>

                    <Grid container spacing={isMobile ? 1 : 3}>
                      <Grid item xs={12} md={3}>
                        <StyledFormControl fullWidth error={!!violationErrors[violation.id]?.violationCode}>
                          <InputLabel size={isMobile ? "small" : "medium"}>Violation Description</InputLabel>
                          <Select
                            value={violation.violationCode || ''}
                            onChange={(e) => {
                              handleViolationChange(index, 'violationCode', e.target.value);
                              clearError(violation.id, 'violationCode');
                            }}
                            disabled={!editModes[violation.id]}
                            label="Violation Description"
                            size={isMobile ? "small" : "medium"}
                          >
                            <MenuItem value="" disabled>
                              Select Violation Type
                            </MenuItem>
                            {allViolations &&
                              allViolations.map((option) => (
                                <MenuItem key={option.id} value={option.violationCode} sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}>
                                  {option.violationDescription}
                                </MenuItem>
                              ))}
                          </Select>
                          {violationErrors[violation.id]?.violationCode && (
                            <FormHelperText>{violationErrors[violation.id].violationCode}</FormHelperText>
                          )}
                        </StyledFormControl>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <DatePickerViolation
                          label="Violation Date"
                          value={violation.date}
                          onChange={(newDate) => {
                            handleViolationChange(index, 'date', newDate);
                            clearError(violation.id, 'date');
                          }}
                          maxDate={dayjs()}
                          disabled={!editModes[violation.id]}
                          error={!!violationErrors[violation.id]?.date}
                          helperText={violationErrors[violation.id]?.date}
                          fullWidth={true}
                          size={isMobile ? "small" : "medium"}
                          startAdornment={<CalendarTodayIcon sx={{ mr: 1, color: '#64748b' }} fontSize={isMobile ? "small" : "medium"} />}
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Typography variant="body2" color="#64748b" fontWeight="500">
                            Time Since Violation
                          </Typography>
                          {violation.timeSince ? (
                            <TimeSinceChip
                              icon={<AccessTimeIcon fontSize={isMobile ? "small" : "medium"} />}
                              label={violation.timeSince}
                              sx={{ alignSelf: 'flex-start' }}
                              size={isMobile ? "small" : "medium"}
                            />
                          ) : (
                            <Typography variant="body2" color="#94a3b8" fontStyle="italic">
                              Select a date to see time elapsed
                            </Typography>
                          )}
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <Box sx={{
                          display: 'flex',
                          gap: 1,
                          justifyContent: isMobile ? 'center' : 'flex-start',
                          flexWrap: isMobile ? 'wrap' : 'nowrap'
                        }}>
                          {editModes[violation.id] && (
                            <Button
                              variant="outlined"
                              onClick={() => handleSaveViolation(index)}
                              disabled={isSaving}
                              size={isMobile ? "small" : "medium"}
                            >
                              {isSaving ? (
                                <CircularProgress size={20} color="inherit" />
                              ) : (
                                <>
                                Save
                                </>
                              )}
                            </Button>
                          )}

                          {violation.isExisting && !editModes[violation.id] && (
                            <ActionButton
                              variant="edit"
                              onClick={() => handleEditViolation(index)}
                              disabled={isSaving || isDeleting}
                              size={isMobile ? "small" : "medium"}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: isMobile ? '0.65rem' : '0.75rem' }}>
                                <EditIcon fontSize={isMobile ? "small" : "medium"} />
                                <span>Edit</span>
                              </Box>
                            </ActionButton>
                          )}

                          <Button
                            variant="outlined"
                            color='error'
                            onClick={() => handleRemoveViolation(index)}
                            disabled={isSaving || isDeleting}
                            size={isMobile ? "small" : "medium"}
                          >
                            {isDeleting ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              
                                <span>Delete</span>
                            )}
                          </Button>
                        </Box>
                        {violationErrors[violation.id]?.general && (
                          <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                            {violationErrors[violation.id].general}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </CardContent>
                </ViolationCard>
              </Fade>
            ))}

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: isMobile ? 2 : 4, mb: -4 }}>
              <Button
                variant='contained'
                startIcon={<AddCircleOutlineIcon />}
                onClick={handleAddViolation}
                disabled={isSaving || isDeleting}
              >
                Add New Violation
              </Button>
            </Box>
          </LocalizationProvider>
        </Container>
      </DialogContent>

      <Divider />
      <DialogActions sx={{ padding: isMobile ? '16px' : '24px 32px', backgroundColor: '#ffffff', gap: 2 }}>
        <Button
          onClick={handleDialogClose}
          disabled={isSaving || isDeleting}
          variant='outlined'
        >
          {isSaving || isDeleting ? 'Processing...' : 'Close'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DriverViolationsDialog;